import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const SubpageHeader = () => (
  <header className="border-b border-[var(--mx-line)] bg-[var(--mx-bg)]/80 backdrop-blur-xl">
    <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 md:px-10">
      <Link to="/" data-testid="subpage-logo" className="flex items-center gap-3">
        <span className="block h-12 overflow-hidden rounded-xl ring-1 ring-[var(--mx-line-strong)]">
          <video autoPlay muted loop playsInline preload="auto" className="h-full w-auto">
            <source src="/metalix-mark.webm" type="video/webm" />
            <source src="/metalix-mark.mp4" type="video/mp4" />
          </video>
        </span>
        <span className="flex flex-col leading-none">
          <span className="font-display text-2xl font-extrabold tracking-tight text-[var(--mx-ink)]">
            Metal<span className="text-[var(--mx-copper)]">ix</span> XT
          </span>
          <span className="mt-0.5 flex w-full justify-between font-mono-tech text-[8px] uppercase text-[var(--mx-ink-2)]">
            {"Private Limited".split("").map((c, i) => (
              <span key={i}>{c === " " ? "\u00A0" : c}</span>
            ))}
          </span>
        </span>
      </Link>
      <Link
        to="/#quote"
        data-testid="subpage-cta"
        className="rounded-full bg-[var(--mx-copper)] px-6 py-2.5 font-mono-tech text-xs uppercase tracking-widest text-white transition-colors hover:bg-[var(--mx-charcoal)]"
      >
        Enquire
      </Link>
    </div>
  </header>
);

export const SubpageLayout = ({ overline, title, accent, intro, children }) => (
  <div className="min-h-screen bg-[var(--mx-bg)]">
    <SubpageHeader />
    <main className="mx-auto max-w-[1400px] px-6 py-20 md:px-10 md:py-28">
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="mb-8 flex items-center gap-4 font-mono-tech text-xs uppercase tracking-[0.25em] text-[var(--mx-ink-2)]">
          <span className="h-px w-10 bg-[var(--mx-copper)]" />
          {overline}
        </div>
        <h1 className="font-display text-5xl font-black uppercase leading-[0.9] tracking-tighter text-[var(--mx-ink)] md:text-7xl">
          {title}
          <br />
          <span className="text-metal">{accent}</span>
        </h1>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-[var(--mx-ink-2)] md:text-lg">
          {intro}
        </p>
      </motion.div>
      {children}
    </main>
    <footer className="border-t border-[var(--mx-line)] px-6 py-8 md:px-10">
      <div className="mx-auto flex max-w-[1400px] flex-col gap-2 font-mono-tech text-xs uppercase tracking-widest text-[var(--mx-ink-3)] md:flex-row md:items-center md:justify-between">
        <span>© {new Date().getFullYear()} Metalix XT Pvt Ltd</span>
        <Link to="/" className="transition-colors hover:text-[var(--mx-copper)]">
          ← Back to metalix-xt.com
        </Link>
      </div>
    </footer>
  </div>
);

export const RoadmapNote = () => (
  <div
    data-testid="roadmap-note"
    className="mt-14 rounded-2xl border border-[var(--mx-copper)]/30 bg-[var(--mx-copper)]/5 p-6 md:p-8"
  >
    <div className="font-mono-tech text-xs uppercase tracking-widest text-[var(--mx-copper)]">
      On our roadmap
    </div>
    <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[var(--mx-ink-2)]">
      This line is part of Metalix XT&apos;s planned expansion beyond lead refining.
      We are actively registering interest from suppliers and buyers — tell us
      about your material or requirement and we will keep you first in line as
      capacity comes online.
    </p>
    <Link
      to="/#quote"
      data-testid="roadmap-cta"
      className="mt-5 inline-block rounded-full bg-[var(--mx-copper)] px-6 py-3 font-mono-tech text-xs uppercase tracking-widest text-white transition-colors hover:bg-[var(--mx-charcoal)]"
    >
      Register interest →
    </Link>
  </div>
);

export const InfoGrid = ({ items }) => (
  <div className="mt-14 grid gap-6 md:grid-cols-3">
    {items.map((it) => (
      <div
        key={it.title}
        className="rounded-2xl border border-[var(--mx-line)] bg-[var(--mx-surface)] p-6 transition-colors duration-300 hover:border-[var(--mx-copper)]/40"
      >
        <div className="font-mono-tech text-xs uppercase tracking-widest text-[var(--mx-copper)]">
          {it.kicker}
        </div>
        <h3 className="mt-3 font-display text-xl font-bold text-[var(--mx-ink)]">
          {it.title}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-[var(--mx-ink-2)]">
          {it.body}
        </p>
      </div>
    ))}
  </div>
);
